/*
 * Was die Palette anbietet: Eintraege und Reiter.
 *
 * Bis zum 04.11.2026 war ein Palettenfeld genau eine Widget-Art. Mit den
 * S400-Teilschemen stimmt das nicht mehr: hundertdreissig Teilschemen sind
 * EINE Art (`schematic-part`) mit unterschiedlichem `part`. Ein Eintrag ist
 * deshalb jetzt ein Rezept (Art plus Vorbelegung) mit eigenem Schluessel.
 * Fuer die gewoehnlichen Widgets ist der Schluessel die Art selbst, damit
 * ein Ziehvorgang, der irgendwo noch eine nackte Art traegt, weiter geht.
 *
 * Der Schluessel wandert als Text durch den Ziehvorgang
 * (`WIDGET_KIND_DRAG_TYPE`); die Flaechen geben ihn unveraendert an die
 * Seite zurueck, und die loest ihn hier wieder auf.
 */

import type { WidgetDefinition, WidgetKind } from '../../widgets';
import { WIDGET_CATALOG, WIDGET_CATALOG_LIST, WIDGET_GROUPS } from './widgetCatalog';
import { S400_FAMILIES, S400_PARTS, s400DemoKey, s400PartKey, type S400PartMeta } from '../../parts/s400/index';

export interface PaletteTab {
  key: string;
  label: string;
  hint: string;
}

export interface PaletteItem {
  /** Eindeutig je Palette; fuer gewoehnliche Widgets die Art. */
  key: string;
  kind: WidgetKind;
  tab: string;
  /** Zwischenueberschrift im Reiter (Familie bei den Teilschemen). */
  section: string;
  title: string;
  description: string;
  /** Kurzzeichen, etwa `S3K`. Steht in der Kachel statt des Rastermasses. */
  code?: string;
  colSpan: number;
  rowSpan: number;
  create(id: string): WidgetDefinition;
  preview(): WidgetDefinition | null;
}

/** Reiter der Teilschemen. Eigener Schluessel, keine Widget-Gruppe: es gibt
 *  keine Widget-Art, die nur dort hingehoert, nur Vorbelegungen. */
export const S400_TAB = 's400';

export const PALETTE_TABS: readonly PaletteTab[] = [
  ...WIDGET_GROUPS.map((g) => ({ key: g.key as string, label: g.label, hint: g.hint })),
  { key: S400_TAB, label: 'S400-Teilschemen', hint: 'Teilschemen der Siemens-Climatix-S400-Anwendung, an Datenpunkte gebunden' },
];

function fromCatalog(): PaletteItem[] {
  const label = new Map(WIDGET_GROUPS.map((g) => [g.key, g.label]));
  return WIDGET_CATALOG_LIST
    // Die nackte Art fuer Teilschemen hat keinen Sinn ohne Wahl des
    // Teilschemas; angeboten werden die Teilschemen einzeln im eigenen Reiter.
    .filter((e) => e.kind !== 'schematic-part')
    .map((e) => ({
      key: e.kind,
      kind: e.kind,
      tab: e.group,
      section: label.get(e.group) ?? e.group,
      title: e.title,
      description: e.description,
      colSpan: e.colSpan,
      rowSpan: e.rowSpan,
      create: (id: string) => e.create(id),
      preview: () => e.preview(),
    }));
}

function fromS400(part: S400PartMeta): PaletteItem {
  const family = S400_FAMILIES.find((f) => f.key === part.family)?.label ?? part.family;
  const base = WIDGET_CATALOG['schematic-part'];
  const key = s400PartKey(part.code);
  return {
    key: `schematic-part:${key}`,
    kind: 'schematic-part',
    tab: S400_TAB,
    section: family,
    title: part.title,
    description: `${part.code} ${part.title} (${part.compact ? 'kompakt' : 'voll'}), ${part.fields.length} Datenpunkte. ${family}.`,
    code: part.code,
    colSpan: base.colSpan,
    rowSpan: base.rowSpan,
    create: (id: string) => ({ ...base.create(id), part: key }),
    preview: () => ({
      ...base.create(`vorschau-${part.code}`),
      part: key,
      bindings: Object.fromEntries(part.fields.map((f) => [f.id, s400DemoKey(f.id)])),
    }),
  };
}

export const PALETTE_ITEMS: readonly PaletteItem[] = [
  ...fromCatalog(),
  ...S400_PARTS.map(fromS400),
];

const BY_KEY = new Map(PALETTE_ITEMS.map((i) => [i.key, i]));

/**
 * Eintrag zu einem Schluessel aus dem Ziehvorgang. Eine nackte Art, die
 * keinen eigenen Eintrag hat (`schematic-part`), faellt auf den Katalog
 * zurueck, damit nichts verloren geht.
 */
export function paletteItem(key: string): PaletteItem | undefined {
  const hit = BY_KEY.get(key);
  if (hit) return hit;
  const entry = (WIDGET_CATALOG as Record<string, (typeof WIDGET_CATALOG)[WidgetKind] | undefined>)[key];
  if (!entry) return undefined;
  return {
    key, kind: entry.kind, tab: entry.group, section: entry.group, title: entry.title,
    description: entry.description, colSpan: entry.colSpan, rowSpan: entry.rowSpan,
    create: (id) => entry.create(id), preview: () => entry.preview(),
  };
}

/** Suchtext eines Eintrags, kleingeschrieben. */
export function itemHaystack(item: PaletteItem): string {
  return `${item.title} ${item.description} ${item.kind} ${item.code ?? ''} ${item.section}`.toLowerCase();
}
